import { FC, useEffect, useState } from "react";
import { Box, FormControl, Grid, InputAdornment, MenuItem, IconButton } from "@mui/material";
import InputFilterFieldStyled from "@/components/styled/input.filter.field.styled";
import InputLabelStyled from "@/components/styled/input.label.styled";
import SearchIcon from "@mui/icons-material/Search";
import { useTranslation } from "react-i18next";
import "./users.page.css";
import {
    FilterUsersRequest,
    FilterUsersResponse,
    UserRowResult,
    UserProfileItem,
    FilterProfilesRequest,
    PermissionName,
} from "@/models/user";
import popupService from "@/services/popupService";
import { useNavigate } from "react-router-dom";
import BaseButton from "@/components/common/baseButton";
import filtersStore from "@/stores/filtersStore";
import api from "@/services/api";
import TotalCountComponent from "@/components/common/resultCountComponent";
import StatusSelect from "@/components/common/status.select";
import ClearIcon from "@/assets/icons/clear-icon.svg";
import { checkEditPermissionWithoutAlert, checkPermissionsWithAlert } from "@/util";

const SettingsListUsersPage: FC = () => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [filter, setFilter] = useState(new FilterUsersRequest());
    const [hasNext, setHasNext] = useState<boolean>(true);
    const [searchCounter, setSearchCounter] = useState<number>(0);
    const [isSearch, setIsSearch] = useState<boolean>(false);
    const [users, setUsers] = useState<UserRowResult[]>([]);
    const [totalFilter, setTotalFilter] = useState(0);
    const [total, setTotal] = useState(0);
    const [profiles, setProfiles] = useState<UserProfileItem[]>([]);

    async function getUsers(): Promise<FilterUsersResponse> {
        const filterStore = filtersStore.usersFilterData;
        if (filterStore != null) {
            if (filterStore.pageIndex !== 0) {
                filterStore.pageIndex = 0;
            }
            setFilter(filterStore ?? new FilterUsersRequest());
            const responsePromise = api.getUsers(filterStore ?? new FilterUsersRequest());
            return (await responsePromise).data;
        }

        filtersStore.setUsersFilterData(filter);
        const responsePromise = api.getUsers(filter);
        return (await responsePromise).data;
    }

    async function getProfiles() {
        const response = await api.getUserProfiles(new FilterProfilesRequest());
        setProfiles(response.data.rows);
    }

    async function sortBy(sortBy: string) {
        if (filter.orderBy !== sortBy) {
            setFilter((prevData) => ({
                ...prevData,
                asc: true,
                orderBy: sortBy,
                pageIndex: 0,
            }));
        } else {
            setFilter((prevData) => ({
                ...prevData,
                asc: !filter.asc,
                orderBy: sortBy,
                pageIndex: 0,
            }));
        }
        filtersStore.setUsersFilterData(null);
    }

    function getArrowClass(sort: string) {
        if (sort === filter.orderBy) {
            if (filter.asc) {
                return "top-sort-arrow";
            } else {
                return "bottom-sort-arrow";
            }
        }
        return "icon-SortArrowIcon sort-arrow-icon";
    }

    const startFetching = async () => {
        setIsSearch(() => true);
        try {
            popupService.showLoading();
            const result = await getUsers();
            setHasNext(result.hasNext);
            if (filter.pageIndex === 0) {
                setUsers(result.rows);
                setTotal(result.total);
                setTotalFilter(result.totalFilter);
            } else {
                setUsers((old) => [...old, ...result.rows]);
            }
            setIsSearch(() => false);
            popupService.close();
        } catch (err) {
            popupService.showApiError(err);
            return;
        } finally {
            setIsSearch(() => false);
        }
    };

    const searchUsersClick = () => {
        setUsers([]);
        filtersStore.setUsersFilterData(null);
        setPageIndex(0);
        setSearchCounter((e) => e + 1);
    };

    const setPageIndex = (index?: number) => {
        setFilter({
            ...filter,
            pageIndex: index ?? filter.pageIndex + 1,
        });
    };

    useEffect(() => {
        let ignore = false;
        if (!ignore) {
            startFetching();
        }
        return () => {
            ignore = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filter.pageIndex, filter.orderBy, filter.asc, searchCounter]);

    useEffect(() => {
        getProfiles();
    }, []);

    function clearFilter() {
        filtersStore.setUsersFilterData(null);
        setFilter(() => new FilterUsersRequest());
        setSearchCounter(searchCounter + 1);
    }

    function selectExactUser(userId: number) {
        navigate(`/settings/manage-users/user/${userId}`);
    }

    return (
        <div key="users_list_wrapper" className="users_list_wrapper">
            <Grid
                sx={{
                    background: "#FFFFFF",
                    width: "100%",
                    margin: "0 0 20px",
                }}
            >
                <Grid className="users_management_label" sx={{ marginBottom: "5px" }}>
                    <InputLabelStyled
                        shrink
                        disableAnimation
                        size="normal"
                        htmlFor="searchFilter-freetext"
                        className="free-text-label"
                        sx={{ alignSelf: "flex-end" }}
                    >
                        {t("searchFilter.WhatAreYouLookingFor")}
                    </InputLabelStyled>

                    {checkEditPermissionWithoutAlert(PermissionName.ManageUsers) ? (
                        <BaseButton
                            style={{ width: "100%", maxWidth: "160px" }}
                            onClick={() => selectExactUser(0)}
                            className="basic-button"
                        >
                            {t("settings.users.addNewUser")}
                            <i style={{ marginInlineStart: "7px" }} className="icon-f icon-plus-icon fs-14"></i>
                        </BaseButton>
                    ) : (
                        ""
                    )}
                </Grid>
                <Grid
                    className="user_management_filter"
                    sx={{
                        display: "flex",
                        justifyContent: "start",
                        gap: "15px",
                    }}
                >
                    <FormControl variant="standard" sx={{ width: "25%" }}>
                        <InputFilterFieldStyled
                            className="search-input"
                            onChange={(event) =>
                                setFilter((prevData: FilterUsersRequest) => ({
                                    ...prevData,
                                    keywords: event.target.value,
                                }))
                            }
                            placeholder={`${t("general.SearchByNamePlaceholder")}`}
                            size="small"
                            value={filter.keywords}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon />
                                    </InputAdornment>
                                ),
                            }}
                        ></InputFilterFieldStyled>
                    </FormControl>
                    <FormControl size="small" sx={{ width: "15%" }}>
                        <InputFilterFieldStyled
                            select
                            label={t("user.Profile")}
                            type="search"
                            size="small"
                            value={filter.profileId ?? ""}
                            onChange={(event: any) =>
                                setFilter((prevData) => ({
                                    ...prevData,
                                    profileId: event.target.value,
                                }))
                            }
                            sx={{
                                width: "100%",
                            }}
                            InputProps={{
                                endAdornment: (
                                    <IconButton
                                        className="select-clear-icon"
                                        sx={{
                                            visibility: filter.profileId ? "visible" : "hidden",
                                        }}
                                        onClick={() =>
                                            setFilter((prevData) => ({
                                                ...prevData,
                                                profileId: null,
                                            }))
                                        }
                                    >
                                        <img src={ClearIcon} alt="Clear icon" />
                                    </IconButton>
                                ),
                            }}
                        >
                            {profiles.map((profile) => (
                                <MenuItem key={profile.id} value={profile.id}>
                                    {profile.name}
                                </MenuItem>
                            ))}
                        </InputFilterFieldStyled>
                    </FormControl>
                    <FormControl size="small" sx={{ width: "10%" }}>
                        <StatusSelect
                            value={filter.status}
                            title={t("user.Status")}
                            updateStatus={(args: boolean | null) =>
                                setFilter(() => ({
                                    ...filter,
                                    status: args,
                                }))
                            }
                        ></StatusSelect>
                    </FormControl>
                    <FormControl
                        sx={{
                            display: "flex",
                            flexDirection: "column-reverse",
                        }}
                    >
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                height: "100%",
                            }}
                            className="link-element clear-all-btn"
                            onClick={() => {
                                clearFilter();
                            }}
                        >
                            {t("display.ClearAll")}
                        </Box>
                    </FormControl>
                    <FormControl>
                        <BaseButton
                            style={{ width: "100%", maxWidth: "110px", justifyContent: "center" }}
                            onClick={() => searchUsersClick()}
                            className="basic-button"
                        >
                            {t("general.filter")}
                        </BaseButton>
                    </FormControl>
                </Grid>
            </Grid>
            <TotalCountComponent
                title={t("settings.QuantityOfUsers") as string}
                allCount={total}
                currentCount={totalFilter}
            />
            <div
                id="employee-list-grid user-management-grid"
                className="employee-list-grid user-management-grid"
                style={{ marginInlineEnd: "25px" }}
            >
                <div className="user-settings-fake-thead"></div>
                <table>
                    <thead>
                        <tr>
                            <td onClick={() => sortBy("FirstName")}>
                                {t("user.FirstName")}
                                <i className={`icon-f ${getArrowClass("FirstName")} fs-12`} />
                            </td>
                            <td onClick={() => sortBy("LastName")}>
                                {t("user.LastName")}
                                <i className={`icon-f ${getArrowClass("LastName")} fs-12`} />
                            </td>
                            <td onClick={() => sortBy("Phone")}>
                                {t("forms.CreateGroupOrder.Mobile")}
                                <i className={`icon-f ${getArrowClass("Phone")} fs-12`} />
                            </td>
                            <td onClick={() => sortBy("Email")}>
                                {t("user.Email")}
                                <i className={`icon-f ${getArrowClass("Email")} fs-12`} />
                            </td>
                            <td onClick={() => sortBy("Profile")}>
                                {t("user.Profile")}
                                <i className={`icon-f ${getArrowClass("Profile")} fs-12`} />
                            </td>
                            <td onClick={() => sortBy("Status")}>
                                {t("user.Status")}
                                <i className={`icon-f ${getArrowClass("Status")} fs-12`} />
                            </td>
                            <td></td>
                        </tr>
                    </thead>
                    <tbody>
                        {users.map((user) => (
                            <tr key={user.userId}>
                                <td>{user.firstName}</td>
                                <td>{user.lastName}</td>
                                <td>{user.phoneNumber}</td>
                                <td>{user.email}</td>
                                <td>{user.profileName}</td>
                                <td>
                                    {user.status ? (
                                        <div className="status-element">
                                            <div className="status-color-active"></div>
                                            <div style={{ minWidth: "80px" }}>{t("employee.ActiveStatus")}</div>
                                        </div>
                                    ) : (
                                        <div className="status-element">
                                            <div className="status-color-inactive"></div>
                                            <div style={{ minWidth: "80px" }}>{t("employee.InactiveStatus")}</div>
                                        </div>
                                    )}
                                </td>
                                <td>
                                    {checkEditPermissionWithoutAlert(PermissionName.ManageUsers) ? (
                                        <div
                                            className="text-align-right"
                                            style={{ display: "flex", justifyContent: "flex-end" }}
                                        >
                                            <i
                                                onClick={() => selectExactUser(user.userId)}
                                                className="icon-f icon-PencilIcon fs-20"
                                            ></i>
                                        </div>
                                    ) : (
                                        ""
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default SettingsListUsersPage;
